import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  setDoc, 
  serverTimestamp, 
  arrayUnion 
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { compressImage } from "../services/fileCompression";
import toast from "react-hot-toast";
import { useProfile } from "../context/ProfileContext";
import { AOS_CATEGORIES } from "../config/aosConstants";
import { AnimatePresence, motion } from "framer-motion";

const STATE_OPTIONS = [
  { code: 'NSW', label: 'New South Wales (NSW)' },
  { code: 'VIC', label: 'Victoria (VIC)' },
  { code: 'QLD', label: 'Queensland (QLD)' },
  { code: 'WA', label: 'Western Australia (WA)' },
  { code: 'SA', label: 'South Australia (SA)' },
  { code: 'TAS', label: 'Tasmania (TAS)' },
  { code: 'ACT', label: 'Australian Capital Territory (ACT)' },
  { code: 'NT', label: 'Northern Territory (NT)' },
  { code: 'INT', label: 'Outside Australia (International Profile)' }
];

const ArtistAccountForm = () => {
  const { isDarkMode, switchProfile, refreshContext } = useProfile();
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // --- BASIC PROFILE STATE ---
  const [artistName, setArtistName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // --- LOCATION SELECTOR STATE ---
  const [selectedState, setSelectedState] = useState("");
  const [suburbSearchQuery, setSuburbSearchQuery] = useState("");
  const [allSuburbsInState, setAllSuburbsInState] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);

  // --- UI & MEDIA STATE ---
  const [artistPhoto, setArtistPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedState || selectedState === 'INT') {
      setAllSuburbsInState([]);
      return;
    }

    const fetchStateSuburbs = async () => {
      setIsLoadingSuburbs(true);
      setSelectedSuburb(null);
      setSuburbSearchQuery('');
      try {
        const response = await fetch(
          `https://cdn.jsdelivr.net/gh/schappim/australian-postcodes@master/data/by-state/${selectedState}.csv`
        );
        const csvText = await response.text();
        const lines = csvText.split('\n');
        const parsed = lines
          .slice(1)
          .map(line => {
            const cols = line.split(',');
            return { suburb: cols[1]?.trim(), state: cols[2]?.trim() };
          })
          .filter(item => item.suburb);

        const unique = Array.from(new Map(parsed.map(item => [item.suburb, item])).values());
        setAllSuburbsInState(unique);
      } catch (err) {
        console.error("ARTIST FORM: Suburb sync error", err);
      } finally {
        setIsLoadingSuburbs(false);
      }
    };

    fetchStateSuburbs();
  }, [selectedState]);

  useEffect(() => {
    if (!suburbSearchQuery.trim()) {
      setFilteredSuggestions([]);
      return;
    }
    const query = suburbSearchQuery.toLowerCase();
    const matches = allSuburbsInState
      .filter(item => item.suburb.toLowerCase().startsWith(query))
      .slice(0, 6);
    setFilteredSuggestions(matches);
  }, [suburbSearchQuery, allSuburbsInState]);

  useEffect(() => {
    if (!isStateDropdownOpen) return;
    const handleOutsideClick = () => setIsStateDropdownOpen(false);
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, [isStateDropdownOpen]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const compressedFile = await compressImage(file, { maxSizeMB: 0.5 });
      setArtistPhoto(compressedFile);
      setPhotoPreview(URL.createObjectURL(compressedFile));
    } catch (error) {
      toast.error("Image processing failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalState = selectedState || "";
    const finalCity = selectedState === "INT" 
      ? "International" 
      : selectedSuburb?.suburb || suburbSearchQuery.trim();
    const cleanArtistEmail = email.trim().toLowerCase();
    
    if (!artistName.trim() || !legalName.trim() || !finalCity || !finalState || !cleanArtistEmail) {
      toast.error("Please complete all required profile details, including location.");
      return;
    }

    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user session found.");

      const token = await currentUser.getIdToken();
      const storage = getStorage();

      // 1. Append the artist email to the central user's associatedEmails array
      const userDocRef = doc(db, "users", currentUser.uid);
      await setDoc(
        userDocRef,
        {
          associatedEmails: arrayUnion(cleanArtistEmail)
        },
        { merge: true }
      );

      // 2. Build the artist profile document
      const artistData = {
        artistName: artistName.trim(),
        legalName: legalName.trim(),
        city: finalCity,
        state: finalState,
        email: cleanArtistEmail,
        phoneNumber: phoneNumber.trim() || "",
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        photoURL: null,
        currentBalanceAUD: 0,
        points: 0,
        lifetimePoints: 0,
        type: 'artist'
      };

      const docRef = await addDoc(collection(db, "artist_accounts"), artistData);
      const artistId = docRef.id;
      let finalPhotoURL = null;

      // 3. Handle Avatar Upload
      if (artistPhoto) {
        const fileName = `profile_picture.${artistPhoto.name.split('.').pop()}`;
        const generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateArtistUploadUrl", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            artistId: artistId,
            fileName: fileName,
            contentType: artistPhoto.type
          })
        });

        if (!generateUrlResponse.ok) throw new Error("Could not acquire authorized upload credentials.");

        const { uploadUrl, finalPath } = await generateUrlResponse.json();

        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': artistPhoto.type },
          body: artistPhoto
        });

        finalPhotoURL = await getDownloadURL(ref(storage, finalPath));
        await updateDoc(doc(db, "artist_accounts", artistId), { photoURL: finalPhotoURL });
      }
      
      // 4. Sync AOS Profiles Systems
      await setDoc(doc(db, "aos_profiles", artistId), {
        artistId, 
        artistName: artistName.trim(), 
        currentStatus: !!finalPhotoURL ? AOS_CATEGORIES.STATUS.HYBRID_STATE : AOS_CATEGORIES.STATUS.INCOMPLETE_PROFILE,
        lastUpdated: serverTimestamp()
      });

      switchProfile({ ...artistData, id: artistId, photoURL: finalPhotoURL });
      await refreshContext();
      navigate(`/profile`, { state: { triggerSwitchAccountOverlay: true } });
      toast.success("Artist Account Created Successfully!");

    } catch (error) {
      toast.error(`Setup processing failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const inputTheme = isDarkMode 
    ? "bg-zinc-800 border-zinc-700 text-white focus:border-[#ff00cc]" 
    : "bg-white border-slate-200 text-slate-900 focus:border-blue-500";

  const cardTheme = isDarkMode 
    ? "bg-zinc-900/50 border-zinc-800 text-zinc-300" 
    : "bg-slate-50 border-slate-200 text-slate-600";

  return (
    <div className={`min-h-screen transition-colors duration-500 pb-20 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <form onSubmit={handleSubmit} className={`max-w-2xl mx-auto p-6 md:p-8 mt-10 shadow-2xl rounded-3xl border transition-all ${isDarkMode ? "bg-[#121212] border-zinc-800" : "bg-white border-slate-100"}`}>
        
        <header className="mb-8 text-center">
          <h1 className={`text-3xl font-black italic tracking-tighter ${isDarkMode ? "text-white" : "text-slate-900"}`}>Artist Registration</h1>
          <p className="text-[#ff00cc] text-[10px] font-black uppercase tracking-widest mt-2">Create Your Artist Profile</p>
        </header>

        {/* STAGE NAME & LEGAL NAME */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-2 block">Stage Name</label>
            <input type="text" value={artistName} onChange={(e) => setArtistName(e.target.value)} className={`w-full p-3 rounded-xl outline-none border text-sm font-semibold transition-all ${inputTheme}`} required />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-2 block">Full Legal Name</label>
            <input type="text" value={legalName} onChange={(e) => setLegalName(e.target.value)} className={`w-full p-3 rounded-xl outline-none border text-sm font-semibold transition-all ${inputTheme}`} required />
          </div>
        </div>

        {/* AVATAR UPLOAD */}
        <div className="mb-6">
          <label className={`text-[10px] font-black uppercase tracking-widest mb-2 block ${isDarkMode ? "text-[#ff00cc]" : "text-blue-600"}`}>
            Visual Avatar
          </label>
          <div className={`p-4 border border-dashed rounded-xl flex flex-col items-center transition-colors ${isDarkMode ? "bg-zinc-800/30 border-zinc-700" : "bg-slate-50 border-slate-200"}`}>
            <input type="file" accept="image/*" onChange={handlePhotoChange} id="file-upload" className="hidden" />
            <label htmlFor="file-upload" className={`cursor-pointer font-bold py-2 px-6 rounded-full transition-all mb-2 text-xs ${isDarkMode ? "bg-zinc-700 text-white hover:bg-zinc-600" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>
              Select Image
            </label>
            {photoPreview && (
              <div className="mt-2">
                <img src={photoPreview} alt="Preview" className={`w-20 h-20 rounded-full object-cover border-2 ${isDarkMode ? "border-[#ff00cc]" : "border-blue-500"}`} />
              </div>
            )}
          </div>
        </div>

        {/* CONTACT DETAILS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-2 block">Email Address</label>
            <input type="email" placeholder="Contact Email" value={email} onChange={(e) => setEmail(e.target.value)} className={`w-full p-3 border rounded-xl outline-none text-sm transition-all ${inputTheme}`} required />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-2 block">Mobile Phone Number (Optional)</label>
            <input type="tel" placeholder="+61 XXX XXX XXX" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className={`w-full p-3 border rounded-xl outline-none text-sm tracking-wider transition-all ${inputTheme}`} />
          </div>
        </div>

        {/* LOCATION SELECTOR */}
        <div className={`p-4 rounded-xl border space-y-4 mb-6 ${cardTheme}`}>
          <label className="text-[10px] font-black uppercase text-[#ff00cc] block">Location</label>
          
          <div className="relative">
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-1 block">1. Select State / Territory</label>
            <div 
              onClick={(e) => {
                e.stopPropagation();
                setIsStateDropdownOpen(!isStateDropdownOpen);
              }}
              className={`w-full p-3 rounded-xl border text-sm font-bold cursor-pointer relative flex justify-between items-center select-none ${inputTheme}`}
            >
              <span>
                {selectedState ? (
                  STATE_OPTIONS.find(opt => opt.code === selectedState)?.label
                ) : (
                  <span className="opacity-50">Select State...</span>
                )}
              </span>
              <span className="text-xs transition-transform duration-200" style={{ transform: isStateDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                ▼
              </span>
            </div>

            <AnimatePresence>
              {isStateDropdownOpen && (
                <motion.div 
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  className={`absolute left-0 right-0 mt-2 rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto p-2 border ${
                    isDarkMode ? "bg-zinc-900 border-zinc-700 text-white" : "bg-white border-slate-200 text-slate-800"
                  }`}
                >
                  {STATE_OPTIONS.map((region) => (
                    <button
                      key={region.code}
                      type="button"
                      onClick={() => {
                        setSelectedState(region.code);
                        setIsStateDropdownOpen(false);
                        setSelectedSuburb(null);
                        setSuburbSearchQuery('');
                      }}
                      className={`w-full text-left px-3 py-2 text-xs font-bold rounded-lg transition-colors border-b last:border-none ${
                        isDarkMode ? "border-zinc-800 hover:bg-zinc-800" : "border-slate-100 hover:bg-slate-100"
                      } ${selectedState === region.code ? "text-[#ff00cc]" : ""}`}
                    >
                      {region.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {selectedState && selectedState !== 'INT' && (
            <motion.div 
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative space-y-1"
            >
              <label className="text-[10px] font-black uppercase text-zinc-500 block">2. Search Suburb / City</label>
              <input
                type="text"
                placeholder={isLoadingSuburbs ? "Downloading suburbs..." : "Type suburb or town..."}
                disabled={isLoadingSuburbs}
                value={selectedSuburb ? selectedSuburb.suburb : suburbSearchQuery}
                onChange={(e) => { 
                  setSelectedSuburb(null); 
                  setSuburbSearchQuery(e.target.value); 
                }}
                className={`w-full p-3 rounded-xl outline-none border text-sm font-semibold transition-all ${inputTheme}`}
              />

              {filteredSuggestions.length > 0 && !selectedSuburb && (
                <div className={`absolute left-0 right-0 mt-1 rounded-xl shadow-2xl z-50 max-h-40 overflow-y-auto p-2 border ${
                  isDarkMode ? "bg-zinc-900 border-zinc-700 text-white" : "bg-white border-slate-200 text-slate-800"
                }`}>
                  {filteredSuggestions.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { 
                        setSelectedSuburb(item); 
                        setSuburbSearchQuery(item.suburb); 
                      }}
                      className={`w-full text-left px-3 py-2 text-xs font-bold rounded-lg transition-colors ${
                        isDarkMode ? "hover:bg-zinc-800" : "hover:bg-slate-100"
                      }`}
                    >
                      {item.suburb}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* --- TERMS & CONDITIONS STATEMENT --- */}
        <div className="mb-6 text-center px-2">
          <p className={`text-xs leading-relaxed ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
            By clicking &apos;Create My Artist Account&apos;, you agree to the{" "}
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`font-semibold underline transition-colors ${
                isDarkMode 
                  ? "text-white hover:text-[#ff00cc]" 
                  : "text-slate-900 hover:text-blue-600"
              }`}
            >
              Atomic Media Terms of Service
            </a>{" "}
            (Version 2.3) and Community Guidelines.
          </p>
        </div>

        {/* SUBMIT BUTTON */}
        <div className="mb-6">
          <button 
            type="submit" 
            disabled={loading} 
            className={`w-full p-4 font-black uppercase tracking-widest rounded-xl transition-all shadow-xl disabled:opacity-25 ${
              isDarkMode ? "bg-[#ff00cc] text-white" : "bg-blue-600 text-white"
            }`}
          >
            {loading ? "Creating Profile..." : "Create My Artist Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ArtistAccountForm;